import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { GoogleAuthProvider, signInWithPopup, signInWithRedirect } from 'firebase/auth';
import { useAuth } from '@/context';
import { Button, Input } from '@/components/common';
import { auth } from '@core/infrastructure/firebase';
import { PasswordInput, GoogleSignInButton } from './components';

const LoginWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.background};
  position: relative;
  overflow: hidden;
  padding: ${({ theme }) => theme.spacing.md};
  
  /* Decorative background elements */
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle, rgba(255, 107, 53, 0.08) 0%, transparent 50%);
    animation: pulse 15s ease-in-out infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -50%;
    right: -50%;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle, rgba(255, 179, 71, 0.06) 0%, transparent 50%);
    animation: pulse 20s ease-in-out infinite reverse;
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
  }
  
  @media (max-width: 480px) {
    padding: ${({ theme }) => theme.spacing.sm};
    align-items: flex-start;
    padding-top: ${({ theme }) => theme.spacing.xl};
  }
`;

const LoginCard = styled.div`
  background: ${({ theme }) => theme.colors.cardBgGradient};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing['2xl']};
  width: 100%;
  max-width: 440px;
  margin: ${({ theme }) => theme.spacing.lg};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  border: 1px solid ${({ theme }) => theme.colors.border};
  position: relative;
  z-index: 1;
  
  /* Gradient accent line */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${({ theme }) => theme.colors.primaryGradient};
    border-radius: ${({ theme }) => theme.borderRadius.xl} ${({ theme }) => theme.borderRadius.xl} 0 0;
  }
  
  @media (max-width: 480px) {
    padding: ${({ theme }) => theme.spacing.lg};
    margin: ${({ theme }) => theme.spacing.sm};
    border-radius: ${({ theme }) => theme.borderRadius.lg};
  }
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const Logo = styled.img`
  width: 100px;
  height: 100px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  
  @media (max-width: 480px) {
    width: 100px;
    height: 100px;
  }
`;

const Title = styled.h1`
  font-family: ${({ theme }) => theme.typography.fontFamilyHeading};
  font-size: ${({ theme }) => theme.typography.fontSizes['4xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  background: linear-gradient(135deg, #FF6B35 0%, #FFB347 50%, #FFD700 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  text-align: center;
  
  @media (max-width: 480px) {
    font-size: ${({ theme }) => theme.typography.fontSizes['4xl']};
  }
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  margin-top: ${({ theme }) => theme.spacing.xs};
  text-align: center;
  
  @media (max-width: 480px) {
    font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  padding: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.errorLight};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin: ${({ theme }) => theme.spacing.sm} 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) => theme.colors.border};
  }
`;

const DividerText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
`;

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { signIn, error: authError, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (isAuthenticated) navigate('/', { replace: true });
  }, [isAuthenticated, navigate]);

  React.useEffect(() => {
    if (authError) setError(authError);
  }, [authError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await signIn(email, password);
      toast.success('Welcome back!');
      navigate('/', { replace: true });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to sign in';
      setError(msg.replace('Firebase: ', '').replace(/ \(auth\/.*\)\.?$/, ''));
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Google sign-in: popup with redirect fallback.
   * D1 enforcement: if the Google account has no tenant_id/role custom claims
   * the backend returns 401 → AuthContext sets error → shown via authError effect.
   */
  const handleGoogleSignIn = async () => {
    setError(null);
    setIsGoogleLoading(true);
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      // onAuthStateChanged in AuthContext handles resolveUser + D1 gate.
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      if (
        code === 'auth/popup-closed-by-user' ||
        code === 'auth/cancelled-popup-request'
      ) {
        // User dismissed — silent, not an error.
      } else if (code === 'auth/popup-blocked') {
        // Browser blocked popup — fall back to redirect.
        try {
          await signInWithRedirect(auth, provider);
        } catch (redirectErr: unknown) {
          const msg = redirectErr instanceof Error ? redirectErr.message : 'Google sign-in failed';
          setError(msg.replace('Firebase: ', '').replace(/ \(auth\/.*\)\.?$/, ''));
        }
      } else {
        const msg = err instanceof Error ? err.message : 'Google sign-in failed';
        setError(msg.replace('Firebase: ', '').replace(/ \(auth\/.*\)\.?$/, ''));
      }
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <LoginWrapper data-testid="candeza-auth-login-page">
      <LoginCard>
        <LogoContainer>
          <Logo src="/media/icons/Candeza_logo_light.png" alt="Candeza" />
          <Title>Candeza</Title>
          <Subtitle>Sign in to your portal</Subtitle>
        </LogoContainer>

        {error && <ErrorMessage role="alert" data-testid="candeza-auth-error">{error}</ErrorMessage>}

        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              autoComplete="email"
              data-testid="candeza-auth-email-field"
              data-flow="login-email-input"
            />
          </InputGroup>

          <InputGroup>
            <Label htmlFor="password">Password</Label>
            <PasswordInput
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              autoComplete="current-password"
              data-testid="candeza-auth-password-field"
              data-flow="login-password-input"
            />
          </InputGroup>

          <Button
            type="submit"
            disabled={isSubmitting || isGoogleLoading}
            style={{ width: '100%' }}
            data-testid="candeza-auth-submit-btn"
            data-flow="login-submit-btn"
          >
            {isSubmitting ? 'Signing in...' : 'Sign In'}
          </Button>
        </Form>

        <Divider>
          <DividerText>or</DividerText>
        </Divider>

        <GoogleSignInButton
          onClick={handleGoogleSignIn}
          disabled={isSubmitting}
          isLoading={isGoogleLoading}
        />
      </LoginCard>
    </LoginWrapper>
  );
};

export default Login;
